import type { CanvasFileItem, QuoteCanvasData } from './types'
import {
  emptyCanvasData,
  isCanvasDataEmpty,
  parseCanvasData,
} from './canvasDocument'

const localKeys = {
  positions: (suffix: string) => `request-canvas:${suffix}`,
  comments: (suffix: string) => `request-canvas-comments:${suffix}`,
  files: (suffix: string) => `request-canvas-files:${suffix}`,
  links: (suffix: string) => `request-canvas-links:${suffix}`,
  strokes: (suffix: string) => `request-canvas-strokes:${suffix}`,
  stamps: (suffix: string) => `request-canvas-stamps:${suffix}`,
  document: (suffix: string) => `request-canvas-doc:${suffix}`,
}

const readJson = (key: string): unknown => {
  try {
    const raw = localStorage.getItem(key)
    return raw ? JSON.parse(raw) : null
  } catch {
    return null
  }
}

const writeJson = (key: string, value: unknown) => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch {
    // localStorage unavailable or quota exceeded — layer stays in memory only
  }
}

const stripFileDataUrls = (files: unknown): CanvasFileItem[] => {
  if (!Array.isArray(files)) return []
  return files.map((file) => {
    const item = file as CanvasFileItem & { dataUrl?: string }
    const { dataUrl: _dataUrl, ...rest } = item
    const url =
      rest.url && !rest.url.startsWith('data:') ? rest.url : undefined
    return { ...rest, url }
  })
}

export const importLegacyLocalCanvas = (
  suffix: string,
  { stripDataUrls = true }: { stripDataUrls?: boolean } = {}
): QuoteCanvasData | null => {
  const filesRaw = readJson(localKeys.files(suffix))
  const imported = parseCanvasData({
    version: 1,
    positions: readJson(localKeys.positions(suffix)) ?? {},
    comments: readJson(localKeys.comments(suffix)) ?? [],
    files: stripDataUrls ? stripFileDataUrls(filesRaw) : filesRaw ?? [],
    links: readJson(localKeys.links(suffix)) ?? [],
    strokes: readJson(localKeys.strokes(suffix)) ?? [],
    stamps: readJson(localKeys.stamps(suffix)) ?? [],
    zOrder: [],
  })
  return isCanvasDataEmpty(imported) ? null : imported
}

export const readLocalCanvasDocument = (suffix: string): QuoteCanvasData => {
  const stored = readJson(localKeys.document(suffix))
  if (stored != null) return parseCanvasData(stored)
  return importLegacyLocalCanvas(suffix, { stripDataUrls: false }) ?? emptyCanvasData()
}

export const writeLocalCanvasDocument = (
  suffix: string,
  value: QuoteCanvasData
) => {
  writeJson(localKeys.document(suffix), value)
}

export const clearLocalCanvasKeys = (suffix: string) => {
  Object.values(localKeys).forEach((keyFor) => {
    try {
      localStorage.removeItem(keyFor(suffix))
    } catch {
      // ignore
    }
  })
}
